import OpenAI from 'openai';
import { playlistAgentTools } from './playlistAgentSchema';
import { searchAgentTools } from './searchAgentSchema';
import { firebaseService } from './firebase';

// Constants
const API_BASE_URL = process.env.REACT_APP_OPENAI_API_BASE_URL || undefined;
const DEFAULT_MODEL = process.env.REACT_APP_OPENAI_MODEL || 'gpt-4.1-nano';

// Enable logging for debugging - set to false in production
const DEBUG = process.env.NODE_ENV !== 'production';

// Logger function
const log = (message: string, data?: any) => {
  if (DEBUG) {
    console.log(`[OpenAIService] ${message}`);
    // Only log data if it's not sensitive
    if (data && !isSensitiveData(data)) {
      console.log(data);
    }
  }
};

// Helper to check if data might contain sensitive information
const isSensitiveData = (data: any): boolean => {
  // Check if the data might contain API keys or other sensitive info
  if (typeof data === 'string') {
    return data.includes('sk-') || data.includes('apiKey') || data.includes('key');
  }
  
  if (typeof data === 'object' && data !== null) {
    return Object.keys(data).some(key => 
      key.toLowerCase().includes('key') || 
      key.toLowerCase().includes('token') || 
      key.toLowerCase().includes('secret') ||
      key.toLowerCase().includes('password')
    );
  }
  
  return false;
};

// Get the currently selected model from localStorage or use default
const getSelectedModel = (): string => {
  const savedModel = localStorage.getItem('preferred_ai_model');
  return savedModel || DEFAULT_MODEL;
};

// Cache for the OpenAI client
let cachedOpenAIClient: OpenAI | null = null;
let cachedApiKey: string = '';

// Create a function to get the OpenAI client with the current API key
const getOpenAIClient = async () => {
  // If we already have a client with the same API key, return it
  if (cachedOpenAIClient) {
    // Check if we need to refresh the API key (e.g., once per session)
    // For now, we'll just use the cached client
    return cachedOpenAIClient;
  }
  
  // Try to get API key from Firebase service using the enhanced method
  let apiKey = '';
  
  try {
    // First try to get the API key using the enhanced method
    const userApiKey = await firebaseService.getUserApiKey();
    if (userApiKey) {
      log('Using API key from enhanced getUserApiKey method (key hidden)');
      apiKey = userApiKey;
    } else {
      // Fall back to the original method
      apiKey = firebaseService.getApiKey() || process.env.REACT_APP_OPENAI_API_KEY || '';
      log('Using API key from fallback method');
    }
  } catch (error) {
    // If there's an error, fall back to the original method
    apiKey = firebaseService.getApiKey() || process.env.REACT_APP_OPENAI_API_KEY || '';
    log('Error getting API key from enhanced method, using fallback', error);
  }
  
  if (!apiKey) {
    log('No API key available for OpenAI');
  }
  
  // Create a new client and cache it
  cachedOpenAIClient = new OpenAI({
    apiKey,
    baseURL: API_BASE_URL,
    dangerouslyAllowBrowser: true,
  });
  
  cachedApiKey = apiKey;
  
  return cachedOpenAIClient;
};

// Function to clear the cached client (useful when API key changes)
const clearCachedClient = () => {
  cachedOpenAIClient = null;
  cachedApiKey = '';
  log('Cleared cached OpenAI client');
};

export interface ChatMessage {
  role: 'system' | 'user' | 'assistant' | 'tool';
  content: string;
  tool_call_id?: string;
  name?: string;
  tool_calls?: any[];
}

export const openaiService = {
  /**
   * Clear the cached OpenAI client
   * Call this when the API key changes or when the user logs out
   */
  clearCache: clearCachedClient,
  
  /**
   * Send a message to the OpenAI API with function definitions
   * @param messages - The messages to send to the API
   * @param toolChoice - Whether to use functions automatically, require them, or not use them
   * @param agentType - Which agent's tools to use ('playlist' or 'search')
   */
  async sendMessage(
    messages: ChatMessage[], 
    toolChoice: 'auto' | 'required' | 'none' = 'auto',
    agentType: 'playlist' | 'search' = 'playlist'
  ) {
    try {
      // Get the currently selected model
      const selectedModel = getSelectedModel();
      
      log(`Sending message to OpenAI with ${messages.length} messages`);
      log(`Using model: ${selectedModel}`);
      log(`Tool choice: ${toolChoice}`);
      log(`Agent type: ${agentType}`);
      
      // Get a client with the current API key
      const openai = await getOpenAIClient();
      
      // Select the appropriate tools based on the agent type
      const tools = agentType === 'search' ? searchAgentTools : playlistAgentTools;
      
      // Determine if we need to use a different API based on the model
      let apiOptions: any = {
        model: selectedModel,
        messages: messages as any,
        tools: tools,
        tool_choice: toolChoice,
      };
      
      const response = await openai.chat.completions.create(apiOptions);
      
      log('Received response from OpenAI');
      
      return response;
    } catch (error) {
      log('Error calling OpenAI API:', error);
      throw error;
    }
  },


  async sendFunctionlessMessage(messages: ChatMessage[], model: String = 'gpt-4o') {
    try {
      
      log(`Sending message to OpenAI with ${messages.length} messages`);
      log(`Using model: ${model}`);
      
      // Get a client with the current API key
      const openai = await getOpenAIClient();
      
      // Determine if we need to use a different API based on the model
      let apiOptions: any = {
        model: model,
        messages: messages as any
      };
      
      const response = await openai.chat.completions.create(apiOptions);
      
      log('Received response from OpenAI');
      
      return response;
    } catch (error) {
      log('Error calling OpenAI API:', error);
      throw error;
    }
  }
};
